import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useClickAway } from 'react-use';
import styled from '@emotion/styled';
import { layers } from 'app/spacing';
import AddButton from 'components/Button/AddButton';
import TextInput from 'components/Inputs/TextInput';
import IconAdornment from 'components/Inputs/Adornments/IconAdornment';
import { SuggestionList } from 'components/WYSIWYG/components/SuggestionList';
import { useEditor } from 'components/WYSIWYG';
import { insertMergeField } from 'components/WYSIWYG/utils/commands';
import {
  filterMergeField,
  useMergeFields,
} from 'components/WYSIWYG/use-merge-fields';
import { useGridLikeFlexChildProps } from 'components/StandaloneToolBar/components/GridLikeFlex';
import { TextEditorToolbarSection } from 'components/StandaloneToolBar/styles';

const InsertMergeFieldButton = styled(AddButton)`
  width: 154px;
  padding: 0;
  margin: 0 15px;
  height: unset;
  line-height: unset;
  cursor: pointer;
`;

const ToolbarSuggestionContainer = styled.div`
  position: relative;
`;

const ToolbarSuggestionList = styled.div`
  position: absolute;
  top: 30px;
  left: 5px;
  z-index: ${layers.content(1)};
  width: ${({ wideMergePicker }) => (wideMergePicker ? `360px` : `200px`)};
`;

const StyledTextInput = styled(TextInput)`
  border: 1px solid var(--color-viz-cat-2-primary);
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 0;
`;

const StyledSuggestionList = styled(SuggestionList)`
  border-top-right-radius: 0;
  border-top-left-radius: 0;
`;

const SuggestionWithInput = forwardRef(
  ({ options, onChange, onClose, wideMergePicker, isLoading = false }, ref) => {
    const suggestionListRef = useRef();
    const { t } = useTranslation();
    const [search, setSearch] = useState('');
    const items = useMemo(() => {
      const hasHeaders = Array.isArray(options[0]?.items);
      const optionFilter = filterMergeField(search);

      if (!hasHeaders) {
        return options.filter(optionFilter);
      }

      return options
        .map(({ label, items, key }) => {
          return Array.isArray(items)
            ? {
                label,
                key,
                items: search ? items.filter(optionFilter) : items,
              }
            : { label, items: [], key };
        })
        .filter(({ items }) => items.length);
    }, [options, search]);

    const inputRef = useCallback((node) => {
      node?.querySelector('input')?.focus();
    }, []);

    useEffect(() => {
      const listener = (event) => {
        if (event.key === 'Escape') {
          onClose();
        }
      };
      window.addEventListener('keydown', listener);
      return () => window.removeEventListener('keydown', listener);
    }, [onClose]);

    return (
      <ToolbarSuggestionList
        ref={ref}
        onKeyDown={(event) => suggestionListRef.current?.onKeyDown({ event })}
        wideMergePicker={wideMergePicker}
      >
        <StyledTextInput
          inModal
          ref={inputRef}
          value={search}
          onChange={setSearch}
          placeholder={t('Find Field')}
          endAdornment={<IconAdornment icon="search" />}
        />
        <StyledSuggestionList
          ref={suggestionListRef}
          items={items}
          onSelect={onChange}
          isLoading={isLoading}
          topButton
        />
      </ToolbarSuggestionList>
    );
  }
);

const ToolbarSuggestion = ({
  options,
  onChange,
  isLoading = false,
  wideMergePicker = false,
}) => {
  const ref = useRef();
  const { t } = useTranslation();
  const [show, setShow] = useState(false);

  useClickAway(ref, () => setShow(false));

  return (
    <ToolbarSuggestionContainer ref={ref}>
      <InsertMergeFieldButton
        as="span"
        color="blue"
        className="insert-btn"
        onClick={() => setShow((x) => !x)}
      >
        {t('Insert Merge Field')}
      </InsertMergeFieldButton>
      {show && (
        <SuggestionWithInput
          options={options}
          isLoading={isLoading}
          wideMergePicker={wideMergePicker}
          onClose={() => setShow(false)}
          onChange={(value) => {
            setShow(false);
            onChange(value);
          }}
        />
      )}
    </ToolbarSuggestionContainer>
  );
};

export const InsertMergeFieldToolbarSection = ({
  mergeFields,
  appendMergeFieldExtraSpace,
  wideMergePicker,
}) => {
  const editor = useEditor();
  const [ref, props] = useGridLikeFlexChildProps();
  const hasFocusedRef = useRef(false);
  const { data, isLoading } = useMergeFields({
    listHeaders: true,
    mergeFields,
  });

  useEffect(() => {
    if (editor) {
      const onFocus = () => {
        hasFocusedRef.current = true;
      };

      editor.on('focus', onFocus);

      return () => {
        editor.off('focus', onFocus);
      };
    }
  }, [editor]);

  if (!editor) return null;

  return (
    <TextEditorToolbarSection ref={ref} {...props}>
      <ToolbarSuggestion
        options={data}
        isLoading={isLoading}
        wideMergePicker={wideMergePicker}
        onChange={(mergeFieldData) => {
          const position = hasFocusedRef.current ? null : 'end';

          editor
            .chain()
            .focus(position)
            .command(
              insertMergeField(mergeFieldData, appendMergeFieldExtraSpace)
            )
            .run();
        }}
      />
    </TextEditorToolbarSection>
  );
};
